import { TemplateResult, html, nothing, CSSResultGroup } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { nanoid } from 'nanoid';
import { SlotController } from '../../controllers/slot-controller';
import { BlocksFileActionEvent } from '../../events/blocks-file-action';
import { BlocksFileAddEvent } from '../../events/blocks-file-add';
import type { CommonAriaLiveValue } from '../../util/common-modal';
import { BlocksElement } from '../BlocksElement';
import '../button/button';
import '../field-note/field-note';
import '../icon/icons/close-circle';
import '../icon/icons/document-filled';
import '../icon/icons/file-upload';
import '../icon/icons/success';
import '../icon/icons/trash';
import '../icon/icons/warning-diamond-filled';
import '../loading-indicator/loading-indicator';
import '../table-body/table-body';
import '../table-cell/table-cell';
import '../table-header-cell/table-header-cell';
import '../table-header/table-header';
import '../table-object/table-object';
import '../table-row/table-row';
import '../table/table';
import '../text-passage/text-passage';
import type { FileUploadFeedback, FileUploadStatus, FileUploadTextConfig, FileUploadType, UploadFileConfig } from './file-upload.model';
import { FileUploadProgressStream, FilesStateConfig, defaultTextConfig } from './file-upload.model';
import styles from './file-upload.styles';

/**
 * @slot default - default component content
 * @slot error - error slot
 * @slot field-note - default field-note
 *
 * @fires bl-file-add -  Emitted when file added
 * @fires bl-file-action -  Emitted when we click on file action
 *
 * @csspart base - The component's base wrapper.
 * @csspart part-label - The component's label element.
 * @csspart label-text - The component's label field text.
 * @csspart body - The component's body field.
 * @csspart input - The component's input element.
 * @csspart text - The component's custom drag drop text.
 * @csspart part-default - The component's default slot.
 * @csspart description-wrapper - The component's description wrapper.
 * @csspart upload-icon - The component's upload icon.
 * @csspart description - The component's description text element.
 * @csspart upload-button - The component's upload button element.
 * @csspart small-table - The component's small-table wrapper.
 * @csspart table-wrapper - The component's table wrapper.
 * @csspart table-item - The component's table item field.
 * @csspart name-container - The component's name container field.
 * @csspart document-filled-icon - The component's document filled icon.
 * @csspart item-text - The component's item text field.
 * @csspart item-second - The component's item second wrapper.
 * @csspart uploading-wrapper - The component's uploading wrapper .
 * @csspart uploading-icon - The component's uploading icon .
 * @csspart uploading-text - The component's uploading text.
 * @csspart success-wrapper - The component's success wrapper.
 * @csspart success-icon - The component's success icon.
 * @csspart success-text - The component'success text.
 * @csspart remove-status-wrapper - The component's status remove wrapper.
 * @csspart close-circle-icon - The component's close circle icon.
 * @csspart status-text - The component's status text field.
 * @csspart item-third - The component's item third wrapper.
 * @csspart close-button - The component's close button icon.
 * @csspart file-upload-table - The component's file-upload-table wrapper.
 * @csspart upload-list - The component's upload-list wrapper.
 * @csspart item-header - The component's item header field.
 * @csspart header-row - The component's header-row field.
 * @csspart first-title - The component's first title field.
 * @csspart second-title - The component's second title field.
 * @csspart third-title - The component's third title field.
 * @csspart upload-list-body - The component's upload-list-body field.
 * @csspart item-wrapper - The component's uploading item wrapper.
 * @csspart item-first - The component's item first wrapper.
 * @csspart field-note-wrapper - The component's field-note wrapper.
 * @csspart part-field-note - The component's field-note slot.
 * @csspart part-error - The component's error slot.
 * @csspart part-warning - The component's warning slot.
 * @csspart warning-icon - The component's warning icon.
 */
export class BlocksFileUpload extends BlocksElement {
  /**
   * Add a static formAssociated property to custom element class.
   * This tells the browser to treat the element like a form control
   */
  static formAssociated = true;static styles: CSSResultGroup = styles;

  /**
   * The unique id of the field
   */
  @property({ type: String })
  fieldId: string | undefined;

  /**
   * The form field's label
   */
  @property({ type: String })
  label: string | undefined;

  /**
   * The form field's drag and drop description
   */
  @property({ type: String })
  description: string | undefined;

  /**
   * File upload name attribute property
   */
  @property({ type: String })
  name: string | undefined;

  /**
   * Value
   */
  @property({ type: String })
  value: string | undefined;

  /**
   * Fieldnote text
   */
  @property({ type: String })
  fieldNote: string | undefined;

  /**
   * Errornote text
   */
  @property({ type: String })
  errorNote: string | undefined;

  /**
   * Aria describedby
   * 1) Used to connect the field note in select field to the select menu for accessibility
   */
  @property({ type: String })
  ariaDescribedBy: string | undefined;

  /**
   * Required attribute
   */
  @property({ type: Boolean })
  required = false;

  /**
   * Disabled attribute
   */
  @property({ type: Boolean, reflect: true })
  disabled: boolean | undefined;

  /**
   * Placeholder text
   */
  @property({ type: String })
  placeholder: string | undefined;

  /**
   * Status - success or error
   */
  @property({ type: String })
  status: FileUploadStatus;

  /**
   * Hide label
   */
  @property({ type: Boolean })
  hideLabel: boolean | undefined;

  /**
   * File Upload Feedback
   * tabular - Displays file details in tabular format.
   * compact - Displays file details in compact, card-like format.
   * preview - Hide the feedback section and application will be handling the preview display.
   */
  @property({ type: String })
  feedback: FileUploadFeedback = 'tabular';

  /**
   * Getter and setter methods for textConfig
   * TextConfig is a JSON structure used to configure the text messages for file upload
   * fileFormatErrorText - Text to be displayed when the file uploaded is not as per supported format,
   * fileSizeErrorText - Text to be displayed when the file size exceeds,
   * uploadingText - Text to be displayed when the file is getting uploaded,
   * dragandDropText - Text to be displayed on drag and drop state,
   * tableFileText - Text to be displayed for the first column heading of preview table,
   * tableStatusText - Text to be displayed for the second column heading of preview table,
   * tableActionText - Text to be displayed for the third column heading of preview table,
   * uploadingActionText - Text to be displayed in third column when file is being uploaded
   */

  @property({ type: Object })
  textConfig: FileUploadTextConfig = {
    fileFormatErrorText: defaultTextConfig.fileFormatErrorText,
    fileSizeErrorText: defaultTextConfig.fileSizeErrorText,
    uploadingText: defaultTextConfig.uploadingText,
    dragandDropText: defaultTextConfig.dragandDropText,
    tableFileText: defaultTextConfig.tableFileText,
    tableStatusText: defaultTextConfig.tableStatusText,
    tableActionText: defaultTextConfig.tableActionText,
    uploadingActionText: defaultTextConfig.uploadingActionText,
    uploadInProgressText: defaultTextConfig.uploadInProgressText,
    uploadSuccessText: defaultTextConfig.uploadSuccessText,
    replaceSuccessText: defaultTextConfig.replaceSuccessText
  };

  /**
   * Getter and setter methods for files to upload from the application
   * TextConfig is a JSON structure used to set the status for the files
   */
  @property({ type: Array })
  uploadFiles: FileUploadProgressStream[] | undefined;

  /**
   * Style variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>**minimal** renders a minimal looking version</li>
   * <li>**button** renders a single button version</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  variant: FileUploadType;

  /**
   * Getter and Setter for uploading time
   */

  @property({ type: Number })
  uploadingtime = 1000;

  /**
   * Getter and Setter for file size Validation
   */

  @property({ type: Number })
  fileSizeLimit = 75;

  /**
   * The accept attribute takes as its value a comma-separated list of one or more file types,
   *  or unique file type specifiers, describing which file types to allow.
   * It can be any of the two types:
      1) A valid case-insensitive filename extension, starting with a period (".") character. For example: .jpg, .pdf, or .doc.
      2) A valid MIME type string, with no extensions.
        * The string audio/* meaning "any audio file".
        * The string image/png — Accepts only image files of PNG type
   * accept="image/png" or accept=".png" — Accepts PNG files.
   * accept="image/png, image/jpeg" or accept=".png, .jpg, .jpeg" — Accept PNG or JPEG files.
   * accept="image/*" — Accept any file with an image/* MIME type.
   * accept=".doc,.docx,.xml,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" — accept anything that smells like an MS Word document.
   */

  @property({ type: String })
  accept: string | undefined;

  /**
   * Boolean attribute used to show a small variant for preview table
   */
  @property({ type: Boolean })
  isSmallTable = false;

  /**
   * Multiple attribute
   * 1) Allow multiple files to be added to the file upload
   */
  @property({ type: Boolean })
  multiple: boolean | undefined;

  /**
   * To replace the old file with new file
   */
  @property({ type: Boolean })
  isReplace = false;

  /**
   * To hide action column
   */
  @property({ type: Boolean })
  hideAction = false;

  /**
   * The ARIA live region politeness level for the file-upload status.
   *
   * @type {string}
   * @default "polite"
   */
  @property({ type: String })
  arialive: CommonAriaLiveValue = 'polite';

  /**
   * Files state
   */
  @state()
  private filesState: FilesStateConfig[] = [];

  /**
   * Dragging state
   */
  @state()
  private isDragging: boolean | undefined;

  /**
   * Active state in minimal variant
   */
  @state()
  private activeState: boolean | undefined;

  /**
   * File Upload Status Text
   */
  @state()
  private fileUploadStatusText: string | undefined;

  @query('.cat-c-file-upload__input')
  private fileUploadInput: HTMLInputElement | undefined;

  @state()
  private currentUploadedFiles: UploadFileConfig[] = [];

  private internals_ = this.attachInternals?.();

  // To check if replace enabled when filesState is empty
  private isEmptyFileState: boolean | undefined;

  /**
   * Associates the control with a form element
   */
  get form(): HTMLFormElement | null {
    return this.internals_?.form;
  }

  /**
   * To return variant type button
   */
  private get isButtonVariant() {
    return this.variant === 'button';
  }

  protected slots = new SlotController(this, null, 'field-note', 'error');

  private get hasFieldNoteSlot(): boolean {
    return this.slots.hasSlotted('field-note');
  }
  private get hasErrorNoteSlot(): boolean {
    return this.slots.hasSlotted('error');
  }

  connectedCallback() {
    super.connectedCallback();
    this.fieldId = this.fieldId || nanoid();
  }

  updated(changedProperties: Map<string, unknown>) {
    changedProperties.forEach((oldValue, propName) => {
      if (propName === 'uploadFiles' && this.uploadFiles !== oldValue) {
        setTimeout(() => {
          const updateFiles = this.filesState;
          this.uploadFiles?.forEach((file) => {
            const index = updateFiles.findIndex((fileData: FilesStateConfig) => fileData.fieldId === file.fileId);
            if (index > -1) {
              updateFiles[index].status = file.status;
              updateFiles[index].statusText = file.statusText;
              updateFiles[index].action = file.action;
              updateFiles[index].actionText = file.actionText;
              updateFiles[index].userAction = file.userAction;
              updateFiles[index].preventDelete = file.preventDelete;
            }
          });
          this.filesState = [];
          this.filesState = updateFiles;
          this.setFileUploadStatus(updateFiles);
        }, this.uploadingtime);
      }
    });
    if (changedProperties.has('filesState')) {
      const formData = new FormData();
      this.filesState?.forEach(({ fileObject }) => {
        if (this.name) {
          formData.append(this.name, fileObject?.[1]);
        }
      });
      this.internals_?.setFormValue(formData);
    }
  }

  /**
   * Updates the file upload status for the current uploaded files.
   *
   * @param updateFiles - An array of `FilesStateConfig` objects containing the updated file information.
   *
   * This method iterates over the `currentUploadedFiles` array and finds the corresponding updated file
   * from the `updateFiles` array using the `fieldId`. It then updates the `fileUploadStatusText` with the
   * status of the current uploaded file. Finally, it clears the `currentUploadedFiles` array.
   */
  private setFileUploadStatus(updateFiles: FilesStateConfig[]) {
    let statusArray: Array<string | undefined> = [];
    this.currentUploadedFiles.forEach((currentFile) => {
      const currentUploadedFile = updateFiles.find((file) => file.fieldId === currentFile.fieldId);
      statusArray.push(this.updateFileUploadStatus(currentUploadedFile!));
    });
    this.fileUploadStatusText = statusArray?.length ? statusArray?.join(' ') : '';
    this.currentUploadedFiles = [];
  }

  private getFileSizeBasedOnOS(fileSize: number) {
    //TODO:Since appVersion deprecated Should check alternatives
    if (navigator.appVersion.indexOf('Win') !== -1) {
      return fileSize * 1024 * 1024;
    } else if (navigator.appVersion.indexOf('Mac') !== -1 || navigator.appVersion.indexOf('Linux') !== -1) {
      return fileSize * 1000 * 1000;
    }
  }

  private onDragOver = (e: DragEvent) => {
    e.preventDefault();
    if (!this.isDragging) {
      this.isDragging = true;
    }
  };

  private onDragEnd = (e: DragEvent) => {
    e.preventDefault();
    if (this.isDragging) {
      this.isDragging = false;
    }
  };

  /**
   * Adding scss to button on open of fileExplorer in minimal variant
   */
  private openFileExplorer() {
    if (this.isButtonVariant) {
      this.activeState = true;
    }
  }

  /**
   * Removing scss to button on close of fileExplorer in minimal variant
   */
  private closeFileExplorer() {
    if (this.isButtonVariant) {
      this.activeState = false;
    }
  }

  /**
   * Based on the fieldId update the userAction details and trigger the event with details
   */
  private onFileAction = (fieldId: string) => {
    this.filesState.map((file: FilesStateConfig) => (file.userAction = file.fieldId === fieldId ? file.action : ''));
    this.filesState = this.filesState.filter((file: FilesStateConfig) => file.fieldId !== fieldId || file.preventDelete === true);
    this.dispatch({ eventName: 'bl-file-action', detailObj: { updatedFiles: this.filesState } }) as BlocksFileActionEvent;
    this.fileUploadInput!.value = '';
  };

  /**
   * 1. Copy existing files from state to a new variable, if isReplace as true , empty the files array, so old files will be removed
   * 2. Check if the file already exists in uploaded files and upload only if the file doesn't exist already
   */
  private onFileInputChange = (e: DragEvent | Event) => {
    const input = e.target as HTMLInputElement;
    const fileObjects = window.DragEvent && e instanceof DragEvent ? e.dataTransfer?.files : (e.target as HTMLInputElement)?.files;
    if (!fileObjects || fileObjects.length === 0) return;
    /* 1 */
    const files: FilesStateConfig[] = this.isReplace ? [] : Array.from(this.filesState || []);

    const uploadedFiles: UploadFileConfig[] = [];
    const statusTextArray: Array<string> = [];

    Object.entries(fileObjects)
      .filter((file) => file?.[1] instanceof File)
      .forEach((file: [string, File]) => {
        const fileSizeError = this.checkFileSizeError(file);
        const fileFormatError = this.checkFileFormatError(file);
        const fileObj: UploadFileConfig = {
          fileObject: file,
          status: fileSizeError || fileFormatError ? 'Failed' : 'Uploading',
          fieldId: nanoid(),
          statusText: '',
          fileSizeExceeded: fileSizeError,
          fileFormatError: fileFormatError,
          action: 'cancel',
          actionText: this.textConfig.uploadingActionText
        };
        /* 2 */
        const currentFile = fileObj?.fileObject?.[1]?.name;
        const indexOfFile = files
          ?.map((item: UploadFileConfig) => {
            let fileName = '';
            if (item?.fileObject?.length) {
              fileName = item?.fileObject?.[1]?.name;
            }
            return fileName;
          })
          ?.indexOf(currentFile);
        if (indexOfFile === -1) {
          uploadedFiles.push(fileObj);
          files.push(fileObj as FilesStateConfig);
          this.currentUploadedFiles.push(fileObj);
          statusTextArray.push(this.updateFileUploadStatus(fileObj));
        }
      });

    this.dispatch({ eventName: 'bl-file-add', detailObj: { uploadedFiles } }) as BlocksFileAddEvent;
    this.filesState = files;
    this.fileUploadStatusText = statusTextArray?.length ? statusTextArray?.join(' ') : '';

    // Clear the file input after handling the file upload
    if (input) {
      input.value = '';
    }
  };

  /**
   * Updates the file upload status based on the current file's status and configuration.
   *
   * @param {UploadFileConfig} currentFile - The configuration object for the file being uploaded.
   * @returns {string} - A string representing the file name and its upload status.
   *
   * The function checks the status of the current file and returns a corresponding status message:
   * - If the status is 'Failed', it checks for file size or format errors and returns the appropriate error text.
   * - If the status is 'Uploading', it returns the uploading text.
   * - If the status is 'Success', it returns either the replace success text or the upload success text based on the `isReplace` flag.
   */
  private updateFileUploadStatus(currentFile: UploadFileConfig): string {
    let uploadStatus: string | undefined = '';
    if (currentFile.status === 'Failed') {
      uploadStatus = currentFile.fileSizeExceeded
        ? this.textConfig.fileSizeErrorText
        : currentFile.fileFormatError
          ? this.textConfig.fileFormatErrorText
          : currentFile.statusText;
    } else if (currentFile.status === 'Uploading') {
      uploadStatus = this.textConfig.uploadInProgressText;
      this.isEmptyFileState = !this.filesState?.length;
    } else if (currentFile.status === 'Success') {
      uploadStatus = !this.isEmptyFileState && this.isReplace ? this.textConfig.replaceSuccessText : this.textConfig.uploadSuccessText;
      this.isEmptyFileState = !this.filesState?.length;
    }

    return `${currentFile.fileObject[1]?.name} ${uploadStatus}`;
  }

  private checkFileSizeError(file: [string, File]): boolean {
    const fileSizeBasedOnOS = this.getFileSizeBasedOnOS(this.fileSizeLimit);
    return !!(fileSizeBasedOnOS && file[1].size > fileSizeBasedOnOS);
  }

  /**
   * 1. Split the accept inputs to check the file format. It can be case-insensitive. (https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/accept#unique_file_type_specifiers)
   * 2. Check the file format based on extension/MIME type - Need to handle all 3 types
   *  examples:
   *    i)   image/png - MIME type
   *    ii)  image/* - wildcard MIME type
   *    iii) .png - extension
   */
  private checkFileFormatError(file: [string, File]): boolean {
    if (this.accept === '*') {
      return false;
    }
    const acceptTypesArray = Array.from(new Set(this.accept?.split(',').map((accept) => accept.toLowerCase()))); //1
    if (!acceptTypesArray.length) {
      return false;
    }
    // get MIME type of file - returns `image/png` (i)
    const fileMimeType = file[1]?.type;
    // 'image/png' is valid if 'image/*' is in array of accepted types (wildcard) (ii)
    const fileMimeTypeRoot = fileMimeType?.split('/')?.shift(); //(returns `image`)
    const fileMimeTypeWildcard = `${fileMimeTypeRoot}/*`; // (returns `image/*`)
    // if user renamed file extension(ex: testfile.png to testfile.txt), file may still contain actual extension. ex: testfile.png.txt
    const fileExtArr = file[1]?.name?.split('.'); // returns `[testfile,png,txt]`
    const fileExt = `.${fileExtArr?.pop()?.toLowerCase()}`; // returns `.txt` (iii)

    // file type (or root of mime type if specifying wildcard) must be in array of valid types
    return (
      acceptTypesArray.indexOf(fileMimeType) === -1 && //2
      acceptTypesArray.indexOf(fileMimeTypeWildcard) === -1 &&
      acceptTypesArray.indexOf(fileExt) === -1
    );
  }

  private setAriaDescribedBy() {
    if (this.hasFieldNote()) {
      this.ariaDescribedBy = this.ariaDescribedBy || nanoid();
    }
  }

  private handleUploadButton() {
    this.fileUploadInput?.click();
    this.fileUploadInput?.focus();
  }

  private hasFieldNote() {
    return this.fieldNote || this.hasFieldNoteSlot || this.errorNote || this.hasErrorNoteSlot;
  }

  /**
   * @param statusText - failed status text - compact section
   */
  private failedCompactTpl(statusText: string) {
    return html`
      <div part="status-compact" class="cat-c-file-upload__status cat-c-file-upload__status--failed">
        <cat-icon-warning-diamond-filled
          part="warning-icon"
          exportparts=${this.getExportParts('warning-diamond-filled', 'icon')}
          color="error"
          class="cat-c-input-field__error-icon"
        ></cat-icon-warning-diamond-filled>
        <span part="status-text" class="cat-c-file-upload__status-text">${statusText}</span>
      </div>
    `;
  }

  /**
   * @param statusText - failed status text - Tabular section
   */
  private failedTabularTpl(statusText: string) {
    return html`
      <div part="status-tabular" class="cat-c-file-upload__status cat-c-file-upload__status--failed">
        <cat-icon-warning-diamond-filled
          part="warning-icon"
          exportparts=${this.getExportParts('warning-diamond-filled', 'icon')}
          color="error"
          class="cat-c-input-field__error-icon"
        ></cat-icon-warning-diamond-filled
        ><span part="status-text" class="cat-c-tabular-text">${statusText}</span>
      </div>
    `;
  }

  /**
   * Generates a description string for a given file action.
   *
   * @param file - The file state configuration object containing details about the file.
   * @returns A string that describes the action performed on the file, including the action text, file name, and status text.
   */
  private getFileActionDescription(file: FilesStateConfig): string {
    return `${file.actionText} ${file.fileObject[1].name} ${file.statusText}`;
  }

  /**
   * Renders the file upload table.
   *
   * This method generates an HTML template for displaying a table of files being uploaded.
   * The table includes headers for file name, status, and actions, and dynamically renders
   * rows based on the current state of the files.
   *
   * @returns {TemplateResult} The HTML template for the file upload table.
   *
   * @remarks
   * - The table includes different status indicators such as uploading, success, failed, and removed.
   * - The action column includes a button for each file if actions are not hidden.
   * - The method uses the `this.textConfig` object for various text configurations.
   * - The `this.filesState` array is used to map and render each file's row in the table.
   * - The `this.getExportParts` method is used to export parts for various components.
   * - The `this.failedTabularTpl` method is used to render failed status messages.
   */
  private renderFileUploadTable(): TemplateResult {
    return html`
      <cat-table-object part="file-upload-table" exportparts=${this.getExportParts('table-object')}>
        <cat-table part="upload-list" exportparts=${this.getExportParts('table')} class="cat-c-file-upload__list">
          <cat-table-header part="item-header" exportparts=${this.getExportParts('table-header')}>
            <cat-table-row
              part="header-row"
              exportparts=${this.getExportParts('header-row', 'table-row')}
              class="${this.disabled ? 'cat-u-is-hidden' : 'cat-c-file-upload__item cat-c-file-upload__item-header'}"
            >
              <cat-table-header-cell
                part="first-title"
                exportparts=${this.getExportParts('first-title', 'table-header-cell')}
                class="cat-c-file-upload__item-first__title"
                >${this.textConfig.tableFileText}
              </cat-table-header-cell>
              <cat-table-header-cell
                part="second-title"
                exportparts=${this.getExportParts('second-title', 'table-header-cell')}
                class="cat-c-file-upload__item-second__title"
                >${this.textConfig.tableStatusText}
              </cat-table-header-cell>
              ${!this.hideAction
                ? html` <cat-table-header-cell
                    colspan="1"
                    part="third-title"
                    exportparts=${this.getExportParts('third-title', 'table-header-cell')}
                    class="cat-c-file-upload__item-third__title"
                    align="center"
                    >${this.textConfig.tableActionText}</cat-table-header-cell
                  >`
                : nothing}
            </cat-table-row>
          </cat-table-header>
          <cat-table-body part="upload-list-body" exportparts=${this.getExportParts('table-body')}>
            ${this.filesState?.map(
              (file: FilesStateConfig) => html`
                <cat-table-row part="item-wrapper" exportparts=${this.getExportParts('item-wrapper', 'table-row')} class="cat-c-file-upload__item">
                  <cat-table-cell
                    part="item-first"
                    exportparts=${this.getExportParts('item-first', 'table-cell')}
                    class="cat-c-file-upload__item-first"
                  >
                    <span part="item-text" class="cat-c-file-upload__item-text">${file.fileObject[1].name}</span>
                  </cat-table-cell>
                  <cat-table-cell
                    part="item-second"
                    exportparts=${this.getExportParts('item-second', 'table-cell')}
                    class="cat-c-file-upload__item-second"
                  >
                    ${file.status === 'Uploading'
                      ? html`
                          <div part="uploading-wrapper" class="cat-c-file-upload__status cat-c-file-upload__status--uploading" id="upload-part">
                            <cat-loading-indicator
                              part="uploading-icon"
                              exportparts=${this.getExportParts('loading-indicator')}
                              small
                            ></cat-loading-indicator>
                            <span part="uploading-text">${this.textConfig.uploadingText}</span>
                          </div>
                        `
                      : nothing}
                    ${file.status === 'Success'
                      ? html`
                          <div part="success-wrapper" class="cat-c-file-upload__status cat-c-file-upload__status--success">
                            <cat-icon-success part="success-icon" exportparts=${this.getExportParts('success', 'icon')}></cat-icon-success>
                            <span part="success-text">${file.statusText}</span>
                          </div>
                        `
                      : nothing}
                    ${file.status === 'Failed' && file.fileSizeExceeded && !file.fileFormatError
                      ? this.failedTabularTpl(this.textConfig.fileSizeErrorText)
                      : nothing}
                    ${file.status === 'Failed' && file.fileFormatError ? this.failedTabularTpl(this.textConfig.fileFormatErrorText) : nothing}
                    ${file.status === 'Failed' && file.statusText ? this.failedTabularTpl(file.statusText) : nothing}
                    ${file.status === 'Removed' && file.statusText
                      ? html`
                          <div part="remove-status-wrapper" class="cat-c-file-upload__status cat-c-file-upload__status--removed">
                            <cat-icon-close-circle
                              part="close-circle-icon"
                              exportparts=${this.getExportParts('close-circle', 'icon')}
                            ></cat-icon-close-circle>
                            <span part="status-text" class="cat-c-file-upload__status-text">${file.statusText}</span>
                          </div>
                        `
                      : nothing}
                  </cat-table-cell>
                  <cat-table-cell
                    part="item-third"
                    exportparts=${this.getExportParts('item-third', 'table-cell')}
                    class="cat-c-file-upload__item-third"
                    align="center"
                  >
                    ${file.action && file.actionText && !this.hideAction
                      ? html` <cat-button
                          part="close-button"
                          exportparts=${this.getExportParts('close-button', 'button')}
                          class="cat-c-file-upload__close-button"
                          variant="ghost"
                          size="sm"
                          type="button"
                          label=${this.getFileActionDescription(file)}
                          @click=${() => this.onFileAction(file.fieldId)}
                        >
                          ${file.actionText}
                        </cat-button>`
                      : nothing}
                  </cat-table-cell>
                </cat-table-row>
              `
            )}
          </cat-table-body>
        </cat-table>
      </cat-table-object>
    `;
  }

  render(): TemplateResult<1> {
    const componentClassNames = this.componentClassNames('cat-c-file-upload', {
      'cat-is-error': this.status === 'error',
      'cat-is-success': this.status === 'success',
      'cat-is-dragging': this.isDragging === true,
      'cat-c-file-upload--hidden-label': this.hideLabel,
      'cat-c-file-upload--disabled': this.disabled,
      'cat-c-file-upload--button': this.isButtonVariant,
      'cat-c-file_upload_active': this.activeState === true,
      'cat-c-file-upload--has-field-note': this.hasFieldNoteSlot || this.fieldNote,
      'cat-c-file-upload--has-error': this.hasErrorNoteSlot || this.errorNote
    });
    this.setAriaDescribedBy();

    return html`
      <div part="base" class="${componentClassNames}">
        <label part="part-label" class="cat-c-file-upload__label" for="${ifDefined(this.fieldId)}">
          <div part="label-text" class="cat-c-file-upload__label-text">${this.label}</div>
          <div
            part="body"
            class="cat-c-file-upload__body"
            @dragover=${this.onDragOver}
            @dragend=${this.onDragEnd}
            @dragleave=${this.onDragEnd}
            @drop=${this.onDragEnd}
          >
            <input
              part="input"
              class="cat-c-file-upload__input"
              type="file"
              accept="${ifDefined(this.accept)}"
              id="${ifDefined(this.fieldId)}"
              @drop=${this.onFileInputChange}
              name="${ifDefined(this.name)}"
              value=""
              @change=${this.onFileInputChange}
              ?required="${this.required}"
              ?disabled="${this.disabled}"
              aria-describedby="${ifDefined(this.ariaDescribedBy)}"
              aria-invalid="false"
              placeholder="${ifDefined(this.placeholder)}"
              ?multiple="${this.multiple}"
              tabindex=${0}
              @click="${this.openFileExplorer}"
              @focus="${this.closeFileExplorer}"
            />
            ${this.isDragging && this.variant !== 'button'
        ? html`<div part="text" class="cat-c-drop_text">${this.textConfig.dragandDropText}</div>`
        : html` <slot part="part-default" tabindex=${-1}>
                  ${this.description
            ? html`
                        <div part="description-wrapper" class="cat-c-file-upload__description">
                          <cat-icon-file-upload
                            part="upload-icon"
                            exportparts=${this.getExportParts('file-upload', 'icon')}
                            class="cat-file-upload-icon"
                          ></cat-icon-file-upload>
                          <cat-text-passage part="description" exportparts=${this.getExportParts('text-passage')} size="sm">
                            ${this.description}</cat-text-passage
                          >
                        </div>
                        <cat-button
                          part="upload-button"
                          exportparts=${this.getExportParts('upload-button', 'button')}
                          class="cat-file-upload-button"
                          ?disabled=${this.disabled}
                          variant="contrast"
                          @click=${this.handleUploadButton}
                          >${this.label}</cat-button
                        >
                      `
            : nothing}
                </slot>`}
          </div>
        </label>
        <cat-field-note
          part="field-note-wrapper"
          exportparts=${this.getExportParts('field-note')}
          id=${ifDefined(this.fieldNote || this.hasFieldNoteSlot ? this.ariaDescribedBy : undefined)}
          .status=${this.status}
          ?isDisabled=${this.disabled}
          class="cat-file-upload-fieldNote"
        >
          <slot part="part-field-note" name="field-note">${this.fieldNote}</slot>
        </cat-field-note>
        <cat-field-note
          part="field-note-wrapper"
          exportparts=${this.getExportParts('field-note')}
          id=${ifDefined(this.errorNote || this.hasErrorNoteSlot ? this.ariaDescribedBy : undefined)}
          .status=${'error'}
          ?isDisabled=${this.disabled}
          class="cat-file-upload-error-slot"
        >
          <slot part="part-error" name="error">${this.errorNote}</slot>
        </cat-field-note>
        <span class="cat-u-is-vishidden" aria-live=${this.arialive}>${this.fileUploadStatusText}</span>
        ${this.isSmallTable || this.feedback === 'compact'
        ? html`
              <ul part="small-table" class="cat-c-file-upload__list-small-table">
                ${this.filesState?.map(
          (file: FilesStateConfig) => html`
                    <li part="table-wrapper" class="cat-c-file-upload__item-small-table">
                      <div part="table-item" class="cat-c-file-upload__small-table">
                        <div
                          part="name-container"
                          class=${file.status === 'Removed' ? 'cat-c-file-upload__name-container--remove' : 'cat-c-file-upload__name-container'}
                        >
                          <cat-icon-document-filled
                            part="document-filled-icon"
                            exportparts=${this.getExportParts('document-filled', 'icon')}
                            class="cat-c-file-upload__icon"
                          ></cat-icon-document-filled>
                          <span part="item-text" class="cat-c-file-upload__item-text">${file.fileObject[1].name}</span>
                        </div>
                        ${file.status
              ? html`
                              <div part="item-second" class="cat-c-file-upload__item-second">
                                ${file.status === 'Uploading'
                  ? html`
                                      <div
                                        part="uploading-wrapper"
                                        class="cat-c-file-upload__status cat-c-file-upload__status--uploading"
                                        id="upload-part"
                                      >
                                        <cat-loading-indicator
                                          part="uploading-icon"
                                          exportparts=${this.getExportParts('loading-indicator')}
                                          small
                                        ></cat-loading-indicator>
                                        <span part="uploading-text" class="cat-c-file-upload__status-text">${this.textConfig.uploadingText}</span>
                                      </div>
                                    `
                  : nothing}
                                ${file.status === 'Success'
                  ? html`
                                      <div part="success-wrapper" class="cat-c-file-upload__status cat-c-file-upload__status--success">
                                        <cat-icon-success
                                          part="success-icon"
                                          exportparts=${this.getExportParts('success', 'icon')}
                                        ></cat-icon-success>
                                        <span part="success-text" class="cat-c-file-upload__status-text">${file.statusText}</span>
                                      </div>
                                    `
                  : nothing}
                                ${file.status === 'Failed' && file.fileSizeExceeded && !file.fileFormatError
                  ? this.failedCompactTpl(this.textConfig.fileSizeErrorText)
                  : nothing}
                                ${file.status === 'Failed' && file.fileFormatError
                  ? this.failedCompactTpl(this.textConfig.fileFormatErrorText)
                  : nothing}
                                ${file.status === 'Failed' && file.statusText ? this.failedCompactTpl(file.statusText) : nothing}
                                ${file.status === 'Removed' && file.statusText
                  ? html`
                                      <div part="remove-status-wrapper" class="cat-c-file-upload__status cat-c-file-upload__status--removed">
                                        <cat-icon-close-circle
                                          part="close-circle-icon"
                                          exportparts=${this.getExportParts('close-circle', 'icon')}
                                        ></cat-icon-close-circle>
                                        <span part="status-text" class="cat-c-file-upload__status-text">${file.statusText}</span>
                                      </div>
                                    `
                  : nothing}
                              </div>
                            `
              : nothing}
                      </div>
                      ${file.action && file.actionText && !this.hideAction
              ? html`<div part="item-third" class="cat-c-file-upload__item-third">
                            <cat-button
                              part="close-button"
                              exportparts=${this.getExportParts('close-button', 'button')}
                              class="cat-c-file-upload__close-button"
                              variant="ghost"
                              type="button"
                              label=${this.getFileActionDescription(file)}
                              @click=${() => this.onFileAction(file.fieldId)}
                            >
                              ${file.actionText}
                            </cat-button>
                          </div> `
              : nothing}
                    </li>
                  `
        )}
              </ul>
            `
          : nothing}
        ${this.feedback === 'tabular' && !this.isSmallTable ? this.renderFileUploadTable() : nothing}
      </div>
    `;
  }
}

if (customElements.get('cat-file-upload') === undefined) {
  customElements.define('cat-file-upload', BlocksFileUpload);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-file-upload': BlocksFileUpload;
  }
}
